import ipaddr from 'ipaddr.js';

// Client-side mirror of organization-service's CIDR rules (loginIpAllowlist/cidr.ts) for inline feedback.
// The service re-validates on save and stays the source of truth.

export type CidrParseResult = { valid: true; cidr: string } | { valid: false; reason: string };

const PREFIX_LENGTH_PATTERN = /^\d{1,3}$/;

const parseAddress = (value: string) => {
  if (ipaddr.IPv4.isValidFourPartDecimal(value)) return ipaddr.IPv4.parse(value);
  if (!value.includes('%') && ipaddr.IPv6.isValid(value)) return ipaddr.IPv6.parse(value);
  return null;
};

/** Validates a single IP or CIDR range and normalizes it to network form (e.g. 10.0.0.1/24 → 10.0.0.0/24). */
export const parseCidr = (input: string): CidrParseResult => {
  const [addressPart, prefixPart, ...rest] = input.trim().split('/');
  if (!addressPart || rest.length) {
    return { valid: false, reason: 'Enter a single IP address or a CIDR range.' };
  }

  const address = parseAddress(addressPart);
  if (!address) {
    return { valid: false, reason: 'Enter a valid IPv4 or IPv6 address. Hostnames and dash ranges are not supported.' };
  }
  if (address.kind() === 'ipv6' && (address as ipaddr.IPv6).isIPv4MappedAddress()) {
    return { valid: false, reason: 'Enter IPv4-mapped IPv6 addresses in their IPv4 form.' };
  }

  const maxPrefixLength = address.kind() === 'ipv4' ? 32 : 128;
  if (prefixPart !== undefined && (!PREFIX_LENGTH_PATTERN.test(prefixPart) || Number(prefixPart) > maxPrefixLength)) {
    return { valid: false, reason: `Prefix length must be between 0 and ${maxPrefixLength}.` };
  }
  const prefixLength = prefixPart === undefined ? maxPrefixLength : Number(prefixPart);

  const family = address.kind() === 'ipv4' ? ipaddr.IPv4 : ipaddr.IPv6;
  const network = family.networkAddressFromCIDR(`${address.toString()}/${prefixLength}`);
  return { valid: true, cidr: `${network.toString()}/${prefixLength}` };
};

/** True when `ip` falls inside any of the given ranges; invalid entries are ignored. */
export const isIpInCidrs = (ip: string, cidrs: readonly string[]) => {
  if (!ipaddr.isValid(ip)) return false;
  const address = ipaddr.process(ip);

  return cidrs.some(entry => {
    const parsed = parseCidr(entry);
    if (parsed.valid === false) return false;
    const [network, prefixLength] = ipaddr.parseCIDR(parsed.cidr);
    return network.kind() === address.kind() && address.match(network, prefixLength);
  });
};

/** Shows single-host ranges as a bare IP (203.0.113.10/32 → 203.0.113.10). */
export const formatCidr = (cidr: string) => {
  const [address, prefixLength] = cidr.split('/');
  const hostPrefixLength = address.includes(':') ? '128' : '32';
  return prefixLength === hostPrefixLength ? address : cidr;
};
